import { PanelConfiguration, PanelConfigurationDisplayMode, UiConfiguration } from '@ekkogmbh/apisdk';
import { Button, ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { StyledTextField } from '../../Common/Components/Forms/StyledTextField';
import TooltipHardened from 'src/Common/Components/TooltipHardened';
import { CenteredColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import { PickerProfileGloveForm } from './PickerProfileGloveForm';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';

const defaultPanelConfig: PanelConfiguration = {
  displayMode: PanelConfigurationDisplayMode.DEFAULT,
  fieldConfig: { primary: '', secondary: '' },
  infos: {
    hidePrimaryField: false,
    hideSecondaryField: false,
    hideCoordinate: false,
    hideValue: false,
    coordinateParts: 1,
  },
};

interface Props {
  uiConfig: UiConfiguration;
  onChange: (config: UiConfiguration) => void;
}
export const PickerProfileUserInterfaceForm = ({ uiConfig, onChange }: Props): React.JSX.Element => {
  const { sourcePanel, destinationPanel } = uiConfig;

  // set defaults when missing
  useEffect(() => {
    if (sourcePanel !== undefined && destinationPanel !== undefined) {
      return;
    }

    const initConfig: UiConfiguration = {
      sourcePanel: sourcePanel !== undefined ? sourcePanel : { ...defaultPanelConfig },
      destinationPanel: destinationPanel !== undefined ? destinationPanel : { ...defaultPanelConfig },
    };
    onChange(initConfig);
  }, [sourcePanel, destinationPanel, onChange]);

  const setPanel = useCallback(
    (key: keyof UiConfiguration, panel: PanelConfiguration) => {
      const { sourcePanel, destinationPanel } = uiConfig;

      const configUpdate = {
        ...{ sourcePanel, destinationPanel },
        [key]: panel,
      };

      onChange(configUpdate);
    },
    [uiConfig, onChange],
  );

  const fieldSourceToggle = useCallback(
    (
      panelKey: keyof UiConfiguration,
      panelConfig: PanelConfiguration,
      configFieldKey: 'fromMetadataPrimaryField' | 'fromMetadataSecondaryField',
    ) => {
      const isMeta = panelConfig.infos?.[configFieldKey] === true;
      return (
        <TooltipHardened
          tooltip={
            <CenteredColumn>
              <Typography>value extracted from</Typography>
              <Typography fontWeight={700}>{isMeta ? 'pick value metadata' : 'compartment field'}</Typography>
              <Typography variant={'caption'} color={'secondary'}>
                click to toggle
              </Typography>
            </CenteredColumn>
          }
        >
          <Button
            variant={'text'}
            color={'secondary'}
            onClick={() =>
              setPanel(panelKey, {
                ...panelConfig,
                infos: {
                  ...panelConfig.infos,
                  [configFieldKey]: !isMeta,
                },
              })
            }
          >
            {isMeta ? 'metadata' : 'compartment'}
          </Button>
        </TooltipHardened>
      );
    },
    [setPanel],
  );

  return (
    <ReactiveRow spacing={4} alignContent={'stretch'} alignItems={'stretch'}>
      <Stack direction={'column'} spacing={1}>
        <Typography
          variant="overline"
          color="primary"
          style={{ display: 'inline-block', fontWeight: 700, textAlign: 'center', width: '100%' }}
        >
          Source Panel
        </Typography>
        <StyledSelectField
          label="Display Mode"
          value={sourcePanel?.displayMode ?? ''}
          onChange={(e) =>
            setPanel('sourcePanel', {
              ...sourcePanel,
              displayMode: e.target.value as PanelConfiguration['displayMode'],
            })
          }
        >
          {Object.values(PanelConfigurationDisplayMode).map((mode: PanelConfigurationDisplayMode, index: number) => (
            <MenuItem key={index} value={mode}>
              <ListItemText primary={mode} />
            </MenuItem>
          ))}
        </StyledSelectField>
        {sourcePanel?.displayMode && (
          <>
            <StyledTextField
              type="text"
              label="Primary Field"
              value={sourcePanel.fieldConfig?.primary ?? ''}
              onChange={(e) =>
                setPanel('sourcePanel', {
                  ...sourcePanel,
                  fieldConfig: {
                    ...sourcePanel.fieldConfig,
                    primary: e.target.value,
                  },
                })
              }
              endAdornment={fieldSourceToggle('sourcePanel', sourcePanel, 'fromMetadataPrimaryField')}
            />
            <StyledTextField
              type="text"
              label="Secondary Field"
              value={sourcePanel.fieldConfig?.secondary ?? ''}
              onChange={(e) =>
                setPanel('sourcePanel', {
                  ...sourcePanel,
                  fieldConfig: {
                    ...sourcePanel.fieldConfig,
                    secondary: e.target.value,
                  },
                })
              }
              endAdornment={fieldSourceToggle('sourcePanel', sourcePanel, 'fromMetadataSecondaryField')}
            />
            <StyledTextField
              type={'number'}
              label={'Coordinate Parts'}
              tooltip={'amount of coordinate parts shown, starting from the last'}
              value={sourcePanel.infos?.coordinateParts ?? 1}
              onChange={(e) => {
                let input: number = parseInt(e.target.value);
                if (input < 1) {
                  input = 1;
                }
                setPanel('sourcePanel', {
                  ...sourcePanel,
                  infos: {
                    ...sourcePanel.infos,
                    coordinateParts: input,
                  },
                });
              }}
            />
            <StyledFormAccordion label={'Hide Information'}>
              <Stack direction={'row'} spacing={1}>
                <StyledCheckbox
                  label={'Coordinate'}
                  value={sourcePanel.infos?.hideCoordinate ?? false}
                  onChange={(e) => {
                    setPanel('sourcePanel', {
                      ...sourcePanel,
                      infos: {
                        ...sourcePanel.infos,
                        hideCoordinate: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Value'}
                  value={sourcePanel.infos?.hideValue ?? false}
                  onChange={(e) => {
                    setPanel('sourcePanel', {
                      ...sourcePanel,
                      infos: {
                        ...sourcePanel.infos,
                        hideValue: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Primary Field'}
                  value={sourcePanel.infos?.hidePrimaryField ?? false}
                  onChange={(e) => {
                    setPanel('sourcePanel', {
                      ...sourcePanel,
                      infos: {
                        ...sourcePanel.infos,
                        hidePrimaryField: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Secondary Field'}
                  value={sourcePanel.infos?.hideSecondaryField ?? false}
                  onChange={(e) => {
                    setPanel('sourcePanel', {
                      ...sourcePanel,
                      infos: {
                        ...sourcePanel.infos,
                        hideSecondaryField: e.target.checked,
                      },
                    });
                  }}
                />
              </Stack>
            </StyledFormAccordion>
          </>
        )}
        <PickerProfileGloveForm
          panelConfig={sourcePanel}
          onChange={(config) => {
            setPanel('sourcePanel', config);
          }}
        />
      </Stack>
      <Stack direction={'column'} spacing={1}>
        <Typography
          variant="overline"
          color="primary"
          style={{ display: 'inline-block', fontWeight: 700, textAlign: 'center', width: '100%' }}
        >
          Destination Panel
        </Typography>
        <StyledSelectField
          label="Display Mode"
          value={destinationPanel?.displayMode ?? ''}
          onChange={(e) =>
            setPanel('destinationPanel', {
              ...destinationPanel,
              displayMode: e.target.value as PanelConfiguration['displayMode'],
            })
          }
        >
          {Object.values(PanelConfigurationDisplayMode).map((mode: PanelConfigurationDisplayMode, index: number) => (
            <MenuItem key={index} value={mode}>
              <ListItemText primary={mode} />
            </MenuItem>
          ))}
        </StyledSelectField>
        {!!destinationPanel?.displayMode && (
          <>
            <StyledTextField
              type="text"
              label="Primary Field"
              value={destinationPanel.fieldConfig?.primary ?? ''}
              onChange={(e) =>
                setPanel('destinationPanel', {
                  ...destinationPanel,
                  fieldConfig: {
                    ...destinationPanel.fieldConfig,
                    primary: e.target.value,
                  },
                })
              }
              endAdornment={fieldSourceToggle('destinationPanel', destinationPanel, 'fromMetadataPrimaryField')}
            />
            <StyledTextField
              type="text"
              label="Secondary Field"
              value={destinationPanel.fieldConfig?.secondary ?? ''}
              onChange={(e) =>
                setPanel('destinationPanel', {
                  ...destinationPanel,
                  fieldConfig: {
                    ...destinationPanel.fieldConfig,
                    secondary: e.target.value,
                  },
                })
              }
              endAdornment={fieldSourceToggle('destinationPanel', destinationPanel, 'fromMetadataSecondaryField')}
            />
            <StyledTextField
              type={'number'}
              label={'Coordinate Parts'}
              tooltip={'amount of coordinate parts shown, starting from the last'}
              value={destinationPanel.infos?.coordinateParts ?? 1}
              onChange={(e) => {
                let input: number = parseInt(e.target.value);
                if (input < 1) {
                  input = 1;
                }
                setPanel('destinationPanel', {
                  ...destinationPanel,
                  infos: {
                    ...destinationPanel.infos,
                    coordinateParts: input,
                  },
                });
              }}
            />
            <StyledFormAccordion label={'Hide Information'}>
              <Stack direction={'row'}>
                <StyledCheckbox
                  label={'Coordinate'}
                  value={destinationPanel.infos?.hideCoordinate ?? false}
                  onChange={(e) => {
                    setPanel('destinationPanel', {
                      ...destinationPanel,
                      infos: {
                        ...destinationPanel.infos,
                        hideCoordinate: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Value'}
                  value={destinationPanel.infos?.hideValue ?? false}
                  onChange={(e) => {
                    setPanel('destinationPanel', {
                      ...destinationPanel,
                      infos: {
                        ...destinationPanel.infos,
                        hideValue: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Primary Field'}
                  value={destinationPanel.infos?.hidePrimaryField ?? false}
                  onChange={(e) => {
                    setPanel('destinationPanel', {
                      ...destinationPanel,
                      infos: {
                        ...destinationPanel.infos,
                        hidePrimaryField: e.target.checked,
                      },
                    });
                  }}
                />
                <StyledCheckbox
                  label={'Secondary Field'}
                  value={destinationPanel.infos?.hideSecondaryField ?? false}
                  onChange={(e) => {
                    setPanel('destinationPanel', {
                      ...destinationPanel,
                      infos: {
                        ...destinationPanel.infos,
                        hideSecondaryField: e.target.checked,
                      },
                    });
                  }}
                />
              </Stack>
            </StyledFormAccordion>
          </>
        )}
        <PickerProfileGloveForm
          panelConfig={destinationPanel}
          onChange={(panelConig) => {
            setPanel('destinationPanel', panelConig);
          }}
        />
      </Stack>
    </ReactiveRow>
  );
};
